// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    alignment = "alignment",
    customCSS = "customCSS",
    // XNG-Changes
    id = "id",
    openInNewTab = "openInNewTab",
    trackButtonLinkClick = "trackButtonLinkClick",
    buttonLinkClickEventName = "buttonLinkClickEventName",
    buttonLinkClickEventClickText = "buttonLinkClickEventClickText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.id]: string;
        [fields.openInNewTab]: boolean;
        [fields.trackButtonLinkClick]: boolean;
        [fields.buttonLinkClickEventName]: string;
        [fields.buttonLinkClickEventClickText]: string;
    };
}

// Xng-Change
const mapStateToProps = (state: xGoogleAnalyticsState) => ({
    xGoogleAnalytics: state.xGoogleAnalytics,
    isAuthenticated: xIsAuthenticated(state),
});

export interface ButtonStyles {
    wrapperStyles: InjectableCss;
}

const wrapperStyles: InjectableCss = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const CmsButton: FC<Props> = ({ fields, xGoogleAnalytics, isAuthenticated }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();

    // XNG-Changes: check for openInNewTab field
    const onClick = () => {
        if (!link.url) {
            return;
        }

        sendTracking(link.url);

        if (fields.openInNewTab) {
            window.open(link.url);
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = link.url;
            return;
        }

        history.push(link.url);
    };

    const styles = useMergeStyles("button", { wrapperStyles });

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    // Xng-Change
    // WES-1785
    const sendTracking = (url: string) => {
        const dataLayer = getDataLayer();
        
        if (!dataLayer || !fields.trackButtonLinkClick) {
            return;
        }

        const googleAnalyticsData = {
            event: fields.buttonLinkClickEventName,
            click_text: fields.buttonLinkClickEventClickText,
            destination: url,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <ContentWrapper alignment={fields.alignment} {...styles?.wrapperStyles} className="wrapper">
                {/* XNG-Changes: Add id for button */}
                <Button variant={fields.variant} onClick={onClick} className="button" id={fields.id}>
                    {fields.label}
                </Button>
            </ContentWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CmsButton),
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            // XNG-Changes: add button id to be targeted by JS while doing authoring when needed
            {
                name: fields.id,
                displayName: "Button ID",
                editorTemplate: "TextField",
                defaultValue: uniqueId(),
                fieldType: "General",
                tab: basicTab,
                isRequired: false,
            },
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.trackButtonLinkClick,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.buttonLinkClickEventName,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: item => item.fields.trackButtonLinkClick,
            },
            {
                name: fields.buttonLinkClickEventClickText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: item => item.fields.trackButtonLinkClick,
            },
        ],
    },
};

export default widgetModule;
